'use client';

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Benutzer, Rolle } from '@/lib/api/auth-types';
import { setAuthToken } from '@/lib/api/client';

interface AuthState {
  token: string | null;
  benutzer: Benutzer | null;
  istAngemeldet: boolean;
  // Live-Fund 16.09.2026: zustand/persist hydriert den Store aus localStorage
  // erst NACH dem ersten Render (um SSR-Hydration-Mismatches zu vermeiden) -
  // ohne dieses Flag sah AuthGuard bei jedem harten Reload kurzzeitig
  // istAngemeldet=false, leitete faelschlich auf /login um, obwohl eine
  // gueltige Session in localStorage lag. Traf besonders /passwort-festlegen
  // hart: der kurze Ausflug auf /login loeste dort (bei muss_passwort_
  // aendern=true) eine sich wiederholende Redirect-Schleife aus.
  hatHydriert: boolean;
  setHatHydriert: (wert: boolean) => void;
  login: (token: string, benutzer: Benutzer) => void;
  logout: () => void;
  hatRolle: (...rollen: Rolle[]) => boolean;
  benutzerAktualisieren: (patch: Partial<Benutzer>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      benutzer: null,
      istAngemeldet: false,
      hatHydriert: false,
      setHatHydriert: (wert: boolean) => set({ hatHydriert: wert }),

      login: (token: string, benutzer: Benutzer) => {
        setAuthToken(token);
        set({ token, benutzer, istAngemeldet: true });
      },

      logout: () => {
        setAuthToken(null);
        set({ token: null, benutzer: null, istAngemeldet: false });
      },

      hatRolle: (...rollen: Rolle[]) => {
        const { benutzer } = get();
        if (!benutzer) return false;
        if (benutzer.rolle === 'admin') return true;
        return rollen.includes(benutzer.rolle);
      },

      benutzerAktualisieren: (patch: Partial<Benutzer>) => {
        const { benutzer } = get();
        if (!benutzer) return;
        set({ benutzer: { ...benutzer, ...patch } });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        benutzer: state.benutzer,
        istAngemeldet: state.istAngemeldet,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHatHydriert(true);
      },
    },
  ),
);
